import { useEffect, useState, type FormEvent, type ReactNode } from 'react'
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom'
import { HiBgScene } from '@/components/HiBgScene'
import { useAuth } from '@/lib/auth'
import {
  ApiError,
  consumeMicrosoftSsoReturn,
  microsoftLoginUrl,
} from '@/lib/api'
import { homePath, normalizePerfil } from '@/lib/rbac'
import { useLegadoVisual } from '@/lib/legadoVisual'
import './login.css'

/** Login corporativo — animação à esquerda, painel branco à direita. */
export function LoginPage() {
  useLegadoVisual()
  const { login, isAuthenticated, loading, homePath: authHome } = useAuth()
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [erro, setErro] = useState('')
  const [enviando, setEnviando] = useState(false)
  const [ssoIndo, setSsoIndo] = useState(false)
  const [ssoResolvido, setSsoResolvido] = useState(false)

  const redirectTo = params.get('redirect') || authHome

  useEffect(() => {
    const { user, erro: ssoErro } = consumeMicrosoftSsoReturn()
    if (ssoErro) {
      setErro(ssoErro)
      setSsoResolvido(true)
      return
    }
    if (user) {
      const dest =
        params.get('redirect') ||
        homePath(normalizePerfil(user.perfil))
      navigate(dest, { replace: true })
      return
    }
    setSsoResolvido(true)
  }, [navigate, params])

  if (!loading && isAuthenticated) {
    return <Navigate to={redirectTo} replace />
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    setErro('')
    setEnviando(true)
    try {
      const u = await login(email.trim(), password)
      const dest =
        params.get('redirect') ||
        (normalizePerfil(u?.perfil) === 'colab' ? '/app/colaborador' : '/app')
      navigate(dest, { replace: true })
    } catch (err) {
      setErro(err instanceof ApiError ? err.message : 'Falha no login.')
    } finally {
      setEnviando(false)
    }
  }

  function onMicrosoft() {
    setErro('')
    setSsoIndo(true)
    window.location.href = microsoftLoginUrl()
  }

  const shell = (content: ReactNode) => (
    <div className="login-root login-root--immersive">
      <div className="login-stage">{content}</div>
    </div>
  )

  if (!ssoResolvido && !erro) {
    return shell(
      <div className="login-right" style={{ width: '100%', flex: 1 }}>
        <p className="login-hint">Validando sessão Microsoft…</p>
      </div>,
    )
  }

  return shell(
    <>
      <div className="login-left">
        <HiBgScene fixed={false} />
        <div className="login-left-content">
          <div className="brand-mark">
            hi Conecta <span>RH.</span>
          </div>
          <div className="brand-sub">Gestão de pessoas e departamento pessoal.</div>
          <div className="features-list">
            <div className="feature-item">
              <span className="feature-dot" />
              <div className="feature-text">
                <strong>Folha e férias</strong>
                <small>Cálculos, períodos e alertas do departamento pessoal.</small>
              </div>
            </div>
            <div className="feature-item">
              <span className="feature-dot" />
              <div className="feature-text">
                <strong>Colaboradores</strong>
                <small>Cadastro, documentos e jornada no mesmo lugar.</small>
              </div>
            </div>
            <div className="feature-item">
              <span className="feature-dot" />
              <div className="feature-text">
                <strong>Acesso da equipe</strong>
                <small>Entre com conta corporativa (Microsoft Entra ID) ou e-mail e senha.</small>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="login-right">
        <div className="login-panel">
          <div className="login-header">
            <h2>Bem-vindo de volta</h2>
            <p>Entre com e-mail e senha ou com a conta Microsoft da empresa.</p>
          </div>

          {erro ? (
            <div className="error-msg" role="alert">
              {erro}
            </div>
          ) : null}

          <form onSubmit={onSubmit}>
            <label className="field">
              <span>E-mail</span>
              <input
                type="email"
                name="email"
                autoComplete="username"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </label>
            <label className="field">
              <span>Senha</span>
              <input
                type="password"
                name="senha"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </label>
            <button className="btn-login" type="submit" disabled={enviando || loading || ssoIndo}>
              {enviando ? 'Entrando…' : 'Entrar'}
            </button>
          </form>

          <div className="login-divisor" aria-hidden="true">
            ou
          </div>

          <button
            className="btn-login btn-microsoft"
            type="button"
            disabled={enviando || loading || ssoIndo}
            onClick={onMicrosoft}
          >
            <svg width="18" height="18" viewBox="0 0 21 21" aria-hidden="true">
              <rect x="1" y="1" width="9" height="9" fill="#f25022" />
              <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
              <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
              <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
            </svg>
            <span>{ssoIndo ? 'Aguarde…' : 'Entrar com a Microsoft'}</span>
          </button>
          <p className="login-sso-hint">Conta corporativa (Microsoft Entra ID · work or school)</p>

          <p className="login-hint">
            <Link to="/carreiras">Ver carreiras públicas</Link>
          </p>
        </div>
      </div>
    </>,
  )
}
